import { Typography, Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import CustomTabs from '../../Components/Custom Components/CustomTabs/CustomTab';
import ConsultantsTabContent from '../../Components/Consultants/ConsultantsTabContent';
import { useNavigate } from 'react-router-dom';
import CustomDialog from '../../Components/Custom Components/CustomDialog/CustomDialog';
import { useDispatch, useSelector } from 'react-redux';
import {
  getConsultantsTabsDetails,
  saveAnswers,
  aiProposalWritingService,
  fetchProposalWriting,
  aiRfpMatchingService,
  fetchRfpMatching
} from '../../Services/consultantsServices';
import ConsultantsSkeleton from '../../Components/Consultants/ConsultantsSkeleton';

const ConsultantsComp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userWorkspace = useSelector(
    (state) => state?.loginUserData?.user?.selectedWorkspace
  );
  const userProfile = useSelector(
    (state) => state?.loginUserData?.user?.userDetails
  );

  const [showSkeleton, setShowSkeleton] = useState(true);
  const [consultantsTabStructure, setConsultantsTabStructure] = useState({
    tabs: [],
  });
  const [consultantsTabStructureOriginal, setConsultantsTabStructureOriginal] =
    useState({ tabs: [] });
  const [selectedTab, setSelectedTab] = useState(0);

  // ai suggested data
  const [proposalWritingData, setProposalWritingData] = useState(null);
  const [rfpMatchingData, setRfpMatchingData] = useState(null);

  useEffect(() => {
    if (userWorkspace?.hideProfile) {
      navigate('/profile');
    } else if (userProfile?.role && userWorkspace?.role !== 'guest') {
      setProposalWritingData(null);
      setRfpMatchingData(null);
      fetchUserTabData();
      getProposalWritingData();
      getRfpMatchingData();
    }
  }, [userProfile, userWorkspace]);

  const suggestProposalWriting = async () => {
    try {
      let payload = { workspaceid: userWorkspace?.id };
      const response = await aiProposalWritingService(payload);
      return response ? true : false;
    } catch (e) {
      console.error('Unable to suggest proposal writing:', e);
    }
  };

  const suggestRfpMatching = async () => {
    try {
      let payload = { workspaceid: userWorkspace?.id };
      const response = await aiRfpMatchingService(payload);
      return response ? true : false;
    } catch (e) {
      console.error('Unable to suggest rfp matching:', e);
    }
  };

  const getProposalWritingData = async () => {
    try {
      let payload = { workspaceid: userWorkspace?.id };
      const response = await fetchProposalWriting(payload);
      if (response) {
        setProposalWritingData(response?.proposalDraft);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getRfpMatchingData = async () => {
    try {
      let payload = { workspaceid: userWorkspace?.id };
      const response = await fetchRfpMatching(payload);
      if (response) {
        setRfpMatchingData(response?.rfpMatch);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Map to the 10 tabs. Index 6 -> Proposal, Index 9 -> RFP
  const aiSuggestionData = [
    null, null, null, null, null, null,
    proposalWritingData, null, null, rfpMatchingData
  ];

  const aiSuggestionHandlers = [
    null, null, null, null, null, null,
    suggestProposalWriting, null, null, suggestRfpMatching
  ];

  const asdFetchers = [
    null, null, null, null, null, null,
    getProposalWritingData, null, null, getRfpMatchingData
  ];

  const fetchUserTabData = async () => {
    const payload = { workspaceid: userWorkspace?.id };
    const response = await getConsultantsTabsDetails(payload, dispatch);

    if (response && response?.length !== 0) {
      setConsultantsTabStructure({ tabs: [] });
      setConsultantsTabStructureOriginal({ tabs: [] });

      const mutableResponse = JSON.parse(JSON.stringify(response));
      let tabData = [];

      mutableResponse?.forEach((tabItem) => {
        let answeredQuestion = 0;

        for (let i = 0; i < tabItem?.questions?.length; i++) {
          const question = tabItem?.questions[i];

          if (question?.type === 'multiSelect' || question?.type === 'multiselect' || question?.type === 'tags') {
            let answerStringInArray = question?.answer?.split(',');
            let answerArray = [];

            answerStringInArray?.forEach((item) => {
              if (item.trim() !== '') {
                // Ensure answer is not an empty string or just spaces
                let answerLabel = question?.type === 'tags' ? item.trim() : undefined;
                question?.options?.forEach((option) => {
                  if (option?.value === item || option === item) {
                    answerLabel = option?.label || option;
                  }
                });
                if (answerLabel) {
                  answerArray.push({ label: answerLabel, value: item });
                }
              }
            });

            question.answer = answerArray;
            if (answerArray?.length > 0) {
              answeredQuestion++;
            }
          } else if (question?.type === 'select' || question?.type === 'dropdown' || question?.type === 'radio') {
            let answerObject;
            question?.options?.forEach((option) => {
              if (option?.value === question?.answer || option === question?.answer) {
                answerObject = {
                  label: option?.label || option,
                  value: question?.answer,
                };
              }
            });

            question.answer = answerObject;
            if (answerObject) {
              answeredQuestion++;
            }
          } else {
            // For other types like textFieldMulti, text, etc.
            if (question?.answer && question?.answer.trim() !== '') {
              answeredQuestion++;
            }
          }
        }

        let tabDataItem = {
          ...tabItem,
          completionPercentage:
            (answeredQuestion / tabItem?.questions?.length) * 100,
        };
        tabData.push(tabDataItem);
      });

      setConsultantsTabStructure({ tabs: [...tabData] });
      let stringifiedData = JSON.stringify(tabData);
      setConsultantsTabStructureOriginal({ tabs: JSON.parse(stringifiedData) });
      setShowSkeleton(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleDataChange = (questionId, answer) => {
    if (answer === undefined) return;

    const updatedTabs = { ...consultantsTabStructure };
    updatedTabs?.tabs[selectedTab]?.questions?.forEach((question) => {
      if (question?.id === questionId) {
        question.answer = answer;
      }
    });
    updateCompletionPercentages(updatedTabs);
  };

  const updateCompletionPercentages = (tabData) => {
    const updatedTabs = tabData?.tabs?.map((tab) => {
      const answeredQuestions = tab?.questions?.reduce((count, question) => {
        if (
          (question?.type === 'multiSelect' || question?.type === 'multiselect' || question?.type === 'tags') &&
          Array.isArray(question?.answer) &&
          question?.answer?.length > 0
        ) {
          return count + 1;
        } else if ((question?.type === 'select' || question?.type === 'dropdown' || question?.type === 'radio') && question?.answer) {
          return count + 1;
        } else if (
          question?.answer &&
          typeof question?.answer === 'string' &&
          question?.answer?.trim()
        ) {
          return count + 1;
        }
        return count;
      }, 0);

      return {
        ...tab,
        completionPercentage: Math.round(
          (answeredQuestions / tab?.questions?.length) * 100
        ),
      };
    });

    setConsultantsTabStructure({ tabs: updatedTabs });
  };

  const isAnswerChanged = (questionId, newAnswer) => {
    return consultantsTabStructureOriginal?.tabs?.some((tab) =>
      tab?.questions?.some((question) => {
        if (question?.id === questionId) {
          return JSON.stringify(question?.answer) !== JSON.stringify(newAnswer);
        }
        return false;
      })
    );
  };

  const submitProfileData = async (questionId, answer) => {
    if (!isAnswerChanged(questionId, answer)) return;

    let formattedAnswer;
    if (Array.isArray(answer)) {
      formattedAnswer =
        answer?.length > 0 ? answer?.map((item) => item?.value || item).join(',') : ' ';
    } else if (typeof answer === 'object' && answer?.value) {
        formattedAnswer = answer.value;
    } else {
      formattedAnswer = answer || ' ';
    }

    const payload = {
      questionId,
      answer: formattedAnswer,
      workspaceId: userWorkspace?.id,
    };

    const response = await saveAnswers(payload, dispatch);
    if (response) {
      fetchUserTabData();
    }
  };

  return (
    <Box className="mt1 ml1 mr1 pb1_5">
      <Box className="displayFlex justifySpaceBetween">
        <Typography
          className="ml1"
          component="h1"
          variant="dashboardCompHeading"
        >
          Consultant Profile
        </Typography>
      </Box>

      <Box
        ml={2}
        sx={{ overflow: 'hidden', minWidth: '1px', maxWidth: '85vw' }}
      >
        <CustomTabs
          selectedTab={selectedTab}
          handleTabChange={handleTabChange}
          tabData={consultantsTabStructure?.tabs}
        />
      </Box>

      {selectedTab !== undefined && !showSkeleton && userProfile?.role && (
        <ConsultantsTabContent
          tabDataHandler={handleDataChange}
          submitAnswer={submitProfileData}
          tabContent={consultantsTabStructure.tabs[selectedTab]}
          aiSuggestionData={aiSuggestionData[selectedTab]}
          aiSuggestionHandler={aiSuggestionHandlers[selectedTab]}
          asdFetcher={asdFetchers[selectedTab]}
        />
      )}

      {showSkeleton && <ConsultantsSkeleton />}

      {userWorkspace?.role === 'guest' && (
        <CustomDialog handleOpen={true} showClose={false}>
          <Typography textAlign="center" fontWeight="bold">
            Access Denied
          </Typography>
          <Typography textAlign="center" mt={2}>
            Access to profile in guest workspace is denied
          </Typography>
          <Box textAlign="center" mt={3}>
            <Button
              variant="buttonVariant1"
              onClick={() => navigate('/account')}
            >
              Go to Account
            </Button>
          </Box>
        </CustomDialog>
      )}
    </Box>
  );
};

export default ConsultantsComp;
